import React from "react";

/**
 * RemediationChip — a small pill button that shows a finding's current
 * remediation status and opens the RemediationDialog on activation.
 *
 * States (exact wording per PRD §20):
 *   - Open              (no remediation record — gray)
 *   - Under Review      (blue)
 *   - Accepted          (amber)
 *   - Treatment Planned (slate)
 *   - Resolved          (green)
 *
 * The chip is a semantic `<button>` and inherits AIDialog's a11y contract
 * via the caller (RunDetail wraps this in a RemediationDialog trigger).
 */
export const REMEDIATION_STATUS_LABELS = {
  open: "Open",
  under_review: "Under Review",
  accepted: "Accepted",
  treatment_planned: "Treatment Planned",
  resolved: "Resolved",
};

const STATUS_CLASSES = {
  open:
    "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 focus-visible:ring-slate-400",
  under_review:
    "border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 focus-visible:ring-blue-400",
  accepted:
    "border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100 focus-visible:ring-amber-400",
  treatment_planned:
    "border-slate-300 bg-slate-100 text-slate-800 hover:bg-slate-200 focus-visible:ring-slate-500",
  resolved:
    "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 focus-visible:ring-emerald-400",
};

export function RemediationChip({
  status = "open",
  onClick,
  onKeyDown,
  triggerRef,
  ariaExpanded,
  testId,
}) {
  const label = REMEDIATION_STATUS_LABELS[status] || REMEDIATION_STATUS_LABELS.open;
  const cls = STATUS_CLASSES[status] || STATUS_CLASSES.open;
  return (
    <button
      ref={triggerRef}
      type="button"
      onClick={onClick}
      onKeyDown={onKeyDown}
      aria-haspopup="dialog"
      aria-expanded={!!ariaExpanded}
      aria-label={`Remediation status: ${label}. Click to manage.`}
      data-testid={testId}
      data-status={status}
      className={
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 " +
        "text-[10px] font-medium uppercase tracking-wider focus:outline-none " +
        "focus-visible:ring-2 " +
        cls
      }
    >
      <span aria-hidden="true">●</span>
      {label}
    </button>
  );
}
